// A tiny router: History API normally; in the static demo the route lives in the URL hash
// (`…/EB/#/write`), so it runs from any folder without server rules. Routes are written without
// the site prefix: on the 72 BPM sister site ("/72/…", see stream.ts) the router adds and strips it.
import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";
import { DEMO } from "../demo/flag";
import { PREFIX, SITE_NAME } from "../stream";

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", emit);
  if (DEMO) window.addEventListener("hashchange", emit);
}

/** The current route: path and query ("?…" or ""). */
function stripPrefix(path: string): string {
  if (!PREFIX || !path.startsWith(PREFIX)) return path;
  return path.slice(PREFIX.length) || "/";
}

function current(): { path: string; search: string } {
  if (!DEMO) return { path: stripPrefix(location.pathname), search: location.search };
  const h = location.hash.replace(/^#/, "") || "/";
  const q = h.indexOf("?");
  return q < 0 ? { path: stripPrefix(h), search: "" } : { path: stripPrefix(h.slice(0, q)), search: h.slice(q) };
}

/** The current route as one string ("/write?day=3"). */
export function currentRoute(): string {
  const c = current();
  return c.path + c.search;
}

/** The href that shows route `to` (adds the "#" in the demo). */
export function hrefOf(to: string): string {
  if (!to.startsWith("/")) return to;
  const p = PREFIX ? `${PREFIX}${to === "/" ? "" : to}` || "/" : to;
  return DEMO ? `#${p}` : p;
}

/** The full URL of route `to` (for sharing and credits). */
export function absoluteUrl(to: string): string {
  return DEMO ? `${location.origin}${location.pathname}${hrefOf(to)}` : `${location.origin}${hrefOf(to)}`;
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === currentRoute()) return;
  const url = hrefOf(to);
  if (opts.replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
  window.scrollTo(0, 0);
  emit();
}

function subscribe(l: Listener) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function usePath(): string {
  return useSyncExternalStore(
    subscribe,
    () => current().path,
    () => "/",
  );
}

export function useSearch(): URLSearchParams {
  const search = useSyncExternalStore(
    subscribe,
    () => current().search,
    () => "",
  );
  return new URLSearchParams(search);
}

/** Match `pattern` like "/day/:date" against `path`; returns params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const pp = pattern.split("/").filter(Boolean);
  const xs = path.split("/").filter(Boolean);
  if (pattern.endsWith("/*")) {
    const base = pp.slice(0, -1);
    if (xs.length < base.length) return null;
    const out: Record<string, string> = { rest: xs.slice(base.length).join("/") };
    return base.every((seg, i) => seg === xs[i]) ? out : null;
  }
  if (pp.length !== xs.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) out[pp[i].slice(1)] = decodeURIComponent(xs[i]);
    else if (pp[i] !== xs[i]) return null;
  }
  return out;
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!href.startsWith("/")) return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={hrefOf(href)} onClick={handle} {...rest} />;
}

/** Set the document title for a page. */
export function useTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  }, [title]);
}
